import { useCallback, useMemo, useState } from "react";

import { getRandomColor } from "@/design/colors/colors";
import FabBar from "@/design/components/FabBar";
import useGoTo from "@/utils/hooks/useGoTo";
import Valkyrie, { viCalendar, viCalendarDay, viCalendarStars, viPlus } from "@sippy-platform/valkyrie";

import {
  Button,
  Card,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useHotkey } from "@tanstack/react-hotkeys";
import { addDays, areIntervalsOverlapping, endOfYear, format, isBefore, startOfDay, startOfYear } from "date-fns";
import PropTypes from "prop-types";

import HolidayDialog from "./HolidayDialog";
import PublicHolidayDialog from "./PublicHolidayDialog";
import EventView from "./View_Event";
import MonthsView from "./View_Months";

function Calendar({ viewDate, holidaySet, holidaySetDispatch }) {
  const theme = useTheme();
  const matchesSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const { getGoTo } = useGoTo();

  const [holiday, setHoliday] = useState(undefined);
  const [publicHoliday, setPublicHoliday] = useState(false);

  const yearlyHolidays = useMemo(() => {
    return holidaySet.holidays
      .filter((holiday) =>
        areIntervalsOverlapping(
          { start: startOfYear(viewDate), end: endOfYear(viewDate) },
          { start: holiday.start, end: holiday.end },
        ),
      )
      .sort((a, b) => isBefore(b.start, a.start) - 1);
  }, [holidaySet.holidays, viewDate]);

  // Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [positioningProps, setPositioningProps] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event, props) => {
    setAnchorEl(event.currentTarget);
    setPositioningProps(props);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const openHolidayDialog = useCallback(() => {
    setHoliday({
      name: "",
      start: format(startOfDay(new Date()), "yyyy-MM-dd HH:mm:ss"),
      end: format(addDays(startOfDay(new Date()), 1), "yyyy-MM-dd HH:mm:ss"),
      color: getRandomColor()[500],
    });
    setPublicHoliday(false);
    handleClose();
  }, []);

  const openPublicHolidayDialog = useCallback(() => {
    setPublicHoliday(true);
    setHoliday(undefined);
    handleClose();
  }, []);

  // Hotkeys
  useHotkey("Alt+H", () => openHolidayDialog());
  useHotkey("Alt+P", () => openPublicHolidayDialog());

  // View
  const [view, setView] = useState("event");

  return (
    <>
      <Card sx={[matchesSmDown && { mb: 6.5 }]}>
        {!matchesSmDown && (
          <Stack
            direction="row"
            sx={[
              {
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              },
              (view === "event" || matchesSmDown) &&
                ((theme) => ({ borderBottom: `1px solid ${theme.vars.palette.divider}` })),
              {
                p: 2,
              },
            ]}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
              }}
            >
              <IconButton
                onClick={() => setView("event")}
                sx={[
                  { p: 1.5 },
                  view === "event" && {
                    color: theme.vars.palette.primary.main,
                    "&:hover": {
                      bgcolor: (theme) =>
                        `rgba(${theme.vars.palette.primary.mainChannel} / calc(${theme.vars.palette.action.selectedOpacity} + ${theme.vars.palette.action.hoverOpacity}))`,
                    },
                    "&::after": {
                      transition: "all .2s ease-in-out",
                      position: "absolute",
                      backgroundColor: (theme) => theme.vars.palette.primary[500],
                      width: "calc(100% - 66%)",
                      height: 3.5,
                      borderRadius: 2,
                      left: "33%",
                      bottom: 0,
                      content: `""`,
                    },
                  },
                ]}
              >
                <Valkyrie icon={viCalendarDay} />
              </IconButton>
              <IconButton
                onClick={() => setView("months")}
                sx={[
                  { p: 1.5 },
                  view === "months" && {
                    color: theme.vars.palette.primary.main,
                    "&:hover": {
                      bgcolor: (theme) =>
                        `rgba(${theme.vars.palette.primary.mainChannel} / calc(${theme.vars.palette.action.selectedOpacity} + ${theme.vars.palette.action.hoverOpacity}))`,
                    },
                    "&::after": {
                      transition: "all .2s ease-in-out",
                      position: "absolute",
                      backgroundColor: (theme) => theme.vars.palette.primary[500],
                      width: "calc(100% - 66%)",
                      height: 3.5,
                      borderRadius: 2,
                      left: "33%",
                      bottom: 0,
                      content: `""`,
                    },
                  },
                ]}
              >
                <Valkyrie icon={viCalendar} />
              </IconButton>
            </Stack>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Button
                startIcon={<Valkyrie icon={viPlus} />}
                onClick={(e) =>
                  handleClick(e, {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "right",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "right",
                    },
                  })
                }
                variant="contained"
                id="addHolidayButton"
              >
                Add
              </Button>
            </Stack>
          </Stack>
        )}
        {(view === "event" || matchesSmDown) && (
          <EventView yearlyHolidays={yearlyHolidays} viewDate={viewDate} setHoliday={setHoliday} getGoTo={getGoTo} />
        )}
      </Card>
      {view === "months" && !matchesSmDown && (
        <MonthsView yearlyHolidays={yearlyHolidays} viewDate={viewDate} setHoliday={setHoliday} getGoTo={getGoTo} />
      )}
      {holiday && (
        <HolidayDialog
          holidaySet={holidaySet}
          holiday={holiday}
          closeHolidayDialog={() => setHoliday(undefined)}
          holidaySetDispatch={holidaySetDispatch}
        />
      )}
      <PublicHolidayDialog
        dialogOpen={publicHoliday}
        holidaySet={holidaySet}
        closeHolidayDialog={() => setPublicHoliday(false)}
        holidaySetDispatch={holidaySetDispatch}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        {...positioningProps}
        sx={{ mt: positioningProps?.anchorOrigin?.vertical === "bottom" ? 0.5 : -0.5 }}
      >
        <MenuList dense>
          <MenuItem onClick={openHolidayDialog} sx={{ pl: 1, pr: 2 }} id="addHolidayItem">
            <ListItemIcon sx={{ minWidth: "32px !important" }}>
              <Valkyrie icon={viCalendarDay} />
            </ListItemIcon>
            <ListItemText>Holiday</ListItemText>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                ml: 5,
              }}
            >
              H
            </Typography>
          </MenuItem>
          <MenuItem onClick={openPublicHolidayDialog} sx={{ pl: 1, pr: 2 }} id="addPublicHolidayItem">
            <ListItemIcon sx={{ minWidth: "32px !important" }}>
              <Valkyrie icon={viCalendarStars} />
            </ListItemIcon>
            <ListItemText>Public holidays</ListItemText>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                ml: 5,
              }}
            >
              P
            </Typography>
          </MenuItem>
        </MenuList>
      </Menu>
      {matchesSmDown && (
        <FabBar>
          <Button
            startIcon={<Valkyrie icon={viPlus} />}
            onClick={(e) =>
              handleClick(e, {
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "right",
                },
                transformOrigin: {
                  vertical: "bottom",
                  horizontal: "right",
                },
              })
            }
            variant="contained"
          >
            Add
          </Button>
        </FabBar>
      )}
    </>
  );
}

Calendar.propTypes = {
  viewDate: PropTypes.number,
  holidaySet: PropTypes.object,
  holidaySetDispatch: PropTypes.func,
};

export default Calendar;
